import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard/useCopyToClipboard';
import { parseJson, type JsonParseCode } from '@/shared/utils/parse-json/parse-json.util';
import { countLines, offsetToLineColumn } from '@/shared/utils/text-lines/text-lines.util';

import { useStructuredAnnouncer } from '../useStructuredAnnouncer/useStructuredAnnouncer';

export interface JsonEditorFailure {
  /** A catalogue key suffix (`json.reason.<code>`), never an engine message. */
  code: JsonParseCode;
  line: number | null;
  column: number | null;
}

export interface UseJsonEditorLogicInput {
  value: unknown;
  onValueChange: (value: unknown) => void;
  minRows?: number;
  maxRows?: number;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  /** See `JsonEditorProps.onParseFailureChange`. Must be stable — an effect dep. */
  onParseFailureChange?: (isUnparsed: boolean) => void;
}

export interface UseJsonEditorLogic {
  text: string;
  setText: (text: string) => void;
  /** Parses and emits. Called on blur, never per keystroke. */
  commit: () => void;
  failure: JsonEditorFailure | null;
  /** Where the failure is, for "go to the problem". */
  errorOffset: number | null;
  /** The line the failure sits on, so the gutter can mark it. 1-based. */
  errorLine: number | null;
  duplicateKeys: readonly string[];
  rows: number;
  isOverflowing: boolean;
  isExpanded: boolean;
  toggleExpanded: () => void;
  format: () => void;
  copy: () => Promise<void>;
  announcement: string;
}

/** Two spaces, because a person reads and edits these. */
const INDENT = 2;
const DEFAULT_MIN_ROWS = 6;
const DEFAULT_MAX_ROWS = 24;

function pretty(value: unknown): string {
  if (value === undefined) return '';

  try {
    return JSON.stringify(value, null, INDENT);
  } catch {
    // A cyclic value cannot come from an api response, but a `custom` caller
    // could hand one over — showing nothing beats throwing during render.
    return '';
  }
}

/**
 * The raw editor behind a shape no control can express.
 *
 * Three decisions carry it, and all three are about not destroying work.
 *
 * It parses **on blur**, not per keystroke: every intermediate state of typing
 * `{"a": 1}` is invalid JSON, so a per-keystroke parse would flash an error
 * through the whole edit and emit nothing until the last character.
 *
 * Invalid text is **kept**, never reverted. A control that restores the last
 * good value on blur throws away work the operator can still see. The text
 * stays, the failure says WHERE — line and column, from our own scanner rather
 * than from V8's English message (U8) — and nothing is emitted, so the form
 * still holds the last value that parsed.
 *
 * And the height is the **line count**, not a measurement. A code editor does
 * not soft-wrap, so one logical line is exactly one line box: `rows` is exact,
 * needs no `scrollHeight` (which is `0` forever in jsdom) and cannot drift out
 * of step with the gutter beside it.
 */
export function useJsonEditorLogic({
  value,
  onValueChange,
  minRows = DEFAULT_MIN_ROWS,
  maxRows = DEFAULT_MAX_ROWS,
  isDisabled = false,
  isReadOnly = false,
  onParseFailureChange,
}: UseJsonEditorLogicInput): UseJsonEditorLogic {
  const { t } = useTranslation('uiKit');
  const announcer = useStructuredAnnouncer();
  const clipboard = useCopyToClipboard();

  // `null` means "not editing": the field then shows the committed value, so a
  // replacement from outside is picked up without a remount.
  const [draft, setDraft] = useState<string | null>(null);
  /** What the last commit produced, and what the prop looked like at that moment. */
  const [committed, setCommitted] = useState<{
    text: string;
    emitted: unknown;
    seen: unknown;
  } | null>(null);
  const [failure, setFailure] = useState<{ code: JsonParseCode; offset: number | null } | null>(
    null,
  );
  const [duplicateKeys, setDuplicateKeys] = useState<readonly string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * The text on screen.
   *
   * After a commit the operator's OWN text is kept — spacing and all — for as
   * long as the prop still means "nothing happened out there": either the owner
   * applied the value we emitted, or it has not applied anything and the prop is
   * exactly what it was. Only a value matching neither is a real replacement,
   * and only then is the field reprinted from it.
   *
   * Without the second case, a form that validates before applying (or simply
   * re-renders late) would revert the field on blur — and the operator would
   * watch a paste they had just made turn back into the old value.
   */
  const isEcho =
    committed !== null && (Object.is(value, committed.emitted) || Object.is(value, committed.seen));
  const text = draft ?? (isEcho ? committed.text : pretty(value));

  /** The one refusal choke point — see `useKeyValueEditorLogic` for why. */
  const refuse = useCallback((): boolean => {
    if (isDisabled) return true;
    if (!isReadOnly) return false;

    announcer.announceReadOnlyRefusal();
    return true;
  }, [announcer, isDisabled, isReadOnly]);

  const commitText = useCallback(
    (next: string): boolean => {
      if (next.trim() === '') {
        // An emptied JSON field is not `{}` — it is "no value", and the
        // setting's own schema decides what that means.
        setFailure(null);
        setDuplicateKeys([]);
        setDraft(null);
        setCommitted({ text: '', emitted: undefined, seen: value });
        onValueChange(undefined);
        return true;
      }

      const parsed = parseJson(next);

      if (!parsed.ok) {
        setFailure({ code: parsed.code, offset: parsed.offset });
        return false;
      }

      setFailure(null);
      setDuplicateKeys(parsed.warnings.map((warning) => warning.key));
      setDraft(null);
      setCommitted({ text: next, emitted: parsed.value, seen: value });
      onValueChange(parsed.value);

      return true;
    },
    [onValueChange, value],
  );

  const isUnparsed = failure !== null;

  useEffect(() => {
    onParseFailureChange?.(isUnparsed);

    // Reported clean on the way out: a field a locale switch or a `visibleWhen`
    // unmounted must not go on blocking a form that can no longer show it.
    return () => {
      onParseFailureChange?.(false);
    };
  }, [isUnparsed, onParseFailureChange]);

  const lineCount = countLines(text);

  return {
    text,
    setText: useCallback(
      (next: string) => {
        if (refuse()) return;
        setDraft(next);
      },
      [refuse],
    ),
    commit: useCallback(() => {
      if (isDisabled || isReadOnly) return;
      commitText(text);
    }, [commitText, isDisabled, isReadOnly, text]),
    failure: useMemo(() => {
      if (failure === null) return null;
      if (failure.offset === null) return { code: failure.code, line: null, column: null };

      return { code: failure.code, ...offsetToLineColumn(text, failure.offset) };
    }, [failure, text]),
    errorOffset: failure?.offset ?? null,
    errorLine:
      failure?.offset === undefined || failure.offset === null
        ? null
        : offsetToLineColumn(text, failure.offset).line,
    duplicateKeys,
    rows: isExpanded
      ? Math.max(lineCount, minRows)
      : Math.min(Math.max(lineCount, minRows), maxRows),
    isOverflowing: !isExpanded && lineCount > maxRows,
    isExpanded,
    toggleExpanded: useCallback(() => {
      setIsExpanded((current) => !current);
    }, []),
    format: useCallback(() => {
      if (refuse()) return;

      const parsed = parseJson(text);
      if (!parsed.ok) {
        // Nothing is reprinted and nothing is emitted: formatting broken text
        // would either lose it or guess at it.
        setFailure({ code: parsed.code, offset: parsed.offset });
        announcer.say(t('json.formatRefused'));
        return;
      }

      setFailure(null);
      setDuplicateKeys(parsed.warnings.map((warning) => warning.key));
      setDraft(pretty(parsed.value));
      onValueChange(parsed.value);
      announcer.say(t('json.formatted'));
    }, [announcer, onValueChange, refuse, t, text]),
    // Deliberately NOT behind `refuse`: copying is the whole point of a
    // read-only value, and refusing it would leave the operator with a
    // screenshot.
    copy: useCallback(async () => {
      const copied = await clipboard.copy(text);
      announcer.say(copied ? t('json.copied') : t('json.copyFailed'));
    }, [announcer, clipboard, t, text]),
    announcement: announcer.announcement,
  };
}
